import React, {Fragment, useEffect} from "react";
import {Notifier} from "../../../../../documents/document_components";
import {Button, TableCell, TableRow} from "@mui/material";
import {getAttrOptions} from "../../../../../inventory/shared/mapping_templates";
import {arrayMove} from "react-sortable-hoc";
import EnhancedMappingTable from "../../../export/components/EnhancedMappingTable";
import { FilterHeader } from "../../../../../../theme";
import type { SettingsImportProfile } from "../../../../types";

// region TypeScript Interfaces
interface SourceFieldOption {
    field: string;
    [key: string]: unknown;
}

interface TargetFieldOption {
    label?: string;
    [key: string]: unknown;
}

interface EnhancedImportTableProps {
    type: string;
    validationWarnings: string[];
    file?: File | null;
    sourceOptions: SourceFieldOption[];
    targetOptions: TargetFieldOption[];
    profile: SettingsImportProfile;
    setProfile: React.Dispatch<React.SetStateAction<SettingsImportProfile>>;
}
// endregion

export default function EnhancedImportTable(props: EnhancedImportTableProps) {
    const {type, validationWarnings} = props;
    const {targetOptions, sourceOptions} = props;
    const {profile, setProfile} = props;

    const [currentTargetOptions, setCurrentTargetOptions] = React.useState<string[]>([]);
    const [currentSourceOptions, setCurrentSourceOptions] = React.useState<string[]>([]);

    //region Functions
    const onSortEnd = ({oldIndex, newIndex}: {oldIndex: number; newIndex: number}) => {
        setProfile(prevState => (
            {...prevState, Fields: arrayMove(prevState.Fields, oldIndex, newIndex)}
        ))
    }

    const onTargetUpdate = (index: number, value: string) => {
        setProfile(prevState => {
            let newFields = [...prevState.Fields];
            newFields[index].alias = value;

            let updatedTargetOptions: any = (getAttrOptions as any)(newFields, currentTargetOptions);
            updatedTargetOptions = updatedTargetOptions.map((field: any) => field.label ? field.label : field);
            setCurrentTargetOptions(updatedTargetOptions);

            return ({...prevState, Fields: newFields});
        })
    }

    const onSourceUpdate = (index: number, value: string) => {
        setProfile(prevState => {
            let newFields = [...prevState.Fields];
            newFields[index].field = value;
            return ({...prevState, Fields: newFields});
        })
    }

    const onConditionUpdate = (index: number, condition: unknown) => {
        setProfile(prevState => {
            let newItems: any[] = [...prevState.Fields];
            newItems[index].Condition = condition;

            let updatedTargetOptions: any = (getAttrOptions as any)(newItems, currentTargetOptions);
            updatedTargetOptions = updatedTargetOptions.map((field: any) => field.label ? field.label : field);
            setCurrentTargetOptions(updatedTargetOptions);

            return ({...prevState, Fields: newItems});
        });
    }

    const onMappingAdd = () => {
        setProfile(prevState => {
            return ({
                ...prevState,
                Fields: [...prevState.Fields, {field: "", alias: ""}]
            });
        })
    }

    const onMappingDelete = (index: number) => {
        setProfile(prevState => {
            let newFields = [...prevState.Fields];
            newFields.splice(index, 1);
            return ({...prevState, Fields: newFields});
        });
    }
    //endregion

    //region Elements
    const headers = (
        <TableRow>
            <TableCell>
                <FilterHeader>
                    Logicbroker Field
                </FilterHeader>
            </TableCell>

            <TableCell>
                <FilterHeader>
                    Column From File
                </FilterHeader>
            </TableCell>

            <TableCell>
                <FilterHeader>
                    Sample Content
                </FilterHeader>
            </TableCell>

            <TableCell>
                <FilterHeader>
                    Condition
                </FilterHeader>
            </TableCell>

            <TableCell></TableCell>
        </TableRow>
    )

    const extraRows = (item: any) => {
        return (
            <TableCell>
                <FilterHeader
                            style={{
                                maxWidth: "200px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap"
                            }}>
                    {item.content}
                </FilterHeader>
            </TableCell>
        )
    }

    const actions = (
        <>
            <Button variant="outlined" color="primary"
                    onClick={onMappingAdd}
            >
                + Add Mapping
            </Button>
        </>
    )
    //endregion

    useEffect(() => {
        const attributesSetup = () => {
            let options = ["Name", "FriendlyName", "Type", "Description", "Options", "Group", "Tags", "DestinationId", "SyncStatus", "System", "LastModified"];
            setCurrentTargetOptions(options);
        }

        const attributeSetsSetup = () => {
            let options = ["Name", "FriendlyName", "Attributes", "Tags", "DestinationId", "SyncStatus", "System", "LastModified", "Hidden", "HiddenFromParter", "CustomAttributes"];
            setCurrentTargetOptions(options);
        }

        const categoriesSetup = () => {
            let options = ["Id", "ParentId", "Name", "AttributeSet", "DestinationId", "BannerURL", "SyncStatus", "Level", "Children", "Path", "LastModified"];
            setCurrentTargetOptions(options);
        }

        switch (type) {
            case "categories":
                categoriesSetup();
                break;
            case "attributes":
                attributesSetup();
                break;
            case "attributeSets":
                attributeSetsSetup();
                break;
            default:
                categoriesSetup();
                break;
        }

        setCurrentSourceOptions(sourceOptions.map((field: SourceFieldOption) => field.field));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profile, sourceOptions, targetOptions]);

    return (
        <>
            {validationWarnings && validationWarnings.length > 0 ? (
                    <Fragment>
                        {validationWarnings.map((r, i) => (
                            <Notifier color="#FFB600" style={{marginBottom: '5px'}}
                                      key={i}>{r}</Notifier>
                        ))}
                    </Fragment>
                )
                : ''
            }

            <EnhancedMappingTable
                type={"mappings"}
                conditionaEditorTitle={"Edit mapping"}
                disableOrdering={true}

                headers={headers}
                extraRows={extraRows}
                actions={actions}

                items={profile.Fields}
                targetOptions={currentTargetOptions.map(option => ({value: option, label: option}))}
                sourceOptions={currentSourceOptions.map(option => ({value: option, label: option}))}

                onSortEnd={onSortEnd}
                onItemDelete={onMappingDelete}
                onTargetUpdate={onTargetUpdate}
                onSourceUpdate={onSourceUpdate}
                onConditionUpdate={onConditionUpdate}
            />
        </>
    )
}
